import * as Yup from 'yup';
import { useRef, useEffect } from 'react';
import { MRT_SortingState, MRT_PaginationState } from 'material-react-table';

import { GridSortModel, GridPaginationModel } from '@mui/x-data-grid';

import { fDate, fromUtcDay } from './format-time';

// Date filters are day-precision: they travel in the URL as plain yyyy-MM-dd
// and are re-anchored to the local calendar day on the way back in, so a
// copied link selects the same days in any timezone.
function decodeDayFilters<T extends Record<string, unknown>>(filters: T): T {
  return Object.fromEntries(
    Object.entries(filters).map(([key, value]) => [
      key,
      value instanceof Date ? fromUtcDay(value) : value,
    ])
  ) as T;
}

/**
 * Creates a Yup validation schema for pagination parameters (page and pageSize).
 *
 * @param options - Configuration options for the pagination schema
 * @param options.defaultPage - The default page number to use (defaults to 1)
 * @param options.defaultPageSize - The default number of items per page (defaults to 25)
 * @param options.allowedPageSizes - Array of valid page size options (defaults to [25, 50, 100])
 *
 * @returns A Yup object schema with `page` and `pageSize` fields
 */
export function createPaginationSchema(
  options: {
    defaultPage?: number;
    defaultPageSize?: number;
    allowedPageSizes?: number[];
  } = {}
) {
  const { defaultPage = 1, defaultPageSize = 25, allowedPageSizes = [25, 50, 100] } = options;

  return Yup.object({
    page: Yup.number()
      .integer()
      .min(1)
      .default(defaultPage)
      .transform((value) => value || defaultPage),
    pageSize: Yup.number()
      .integer()
      .oneOf(allowedPageSizes)
      .default(defaultPageSize)
      .transform((value) => value || defaultPageSize),
  });
}
/**
 * Creates a Yup validation schema for sort parameters (sortBy and sortOrder).
 *
 * @template T - A readonly array type of allowed sort field names
 * @param allowedSortFields - Array of valid field names that can be sorted
 * @param options - Configuration options for the sort schema
 * @param options.defaultSortOrder - The default sort direction (defaults to 'asc')
 * @param options.defaultSortBy - The default sort field
 *
 * @returns A Yup object schema with `sortBy` and `sortOrder` fields
 */
export function createSortSchema<T extends readonly string[]>(
  allowedSortFields: T,
  options: {
    defaultSortOrder?: 'asc' | 'desc';
    defaultSortBy?: T[number];
  } = {}
) {
  const { defaultSortOrder = 'asc', defaultSortBy } = options;

  return Yup.object({
    sortBy: Yup.string()
      .oneOf([...allowedSortFields])
      .optional()
      .default(defaultSortBy),
    sortOrder: Yup.mixed<'asc' | 'desc'>()
      .oneOf(['asc', 'desc'] as const)
      .optional()
      .when('sortBy', {
        is: (val: string) => !!val,
        then: (schema) => schema.default(defaultSortOrder),
      }),
  });
}

type TableSearchParamsModel = {
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
} & Record<string, unknown>; // Allow additional filter properties with unknown type

/**
 * Converts URL search parameters into MUI DataGrid models for pagination, sorting, and filtering.
 *
 * @template TSearchParams - Type extending TableSearchParamsModel with optional additional filter properties
 * @param searchParams - Object containing page, pageSize, sortBy, sortOrder, and any additional filter properties
 *
 * @returns An object containing:
 * - `paginationModel` - MUI pagination model with 0-indexed page and pageSize (undefined if page/pageSize not provided)
 * - `sortModel` - MUI sort model array with field and sort direction (undefined if sortBy/sortOrder not provided)
 * - `filterModel` - Object containing all additional properties from searchParams (excluding pagination and sort fields)
 */
export function getTableModelsFromSearchParams<TSearchParams extends TableSearchParamsModel>(
  searchParams: TSearchParams
) {
  const { page, pageSize, sortBy, sortOrder, ...rest } = searchParams;

  // MUI uses 0 based page index so subtract one
  const paginationModel = page && pageSize ? { page: page - 1, pageSize } : undefined;
  const sortModel = sortBy && sortOrder ? [{ field: sortBy, sort: sortOrder }] : undefined;
  const filterModel = decodeDayFilters(rest);

  return {
    paginationModel,
    sortModel,
    filterModel,
  };
}

/**
 * Converts URL search parameters into material react table models for pagination, sorting, and filtering.
 *
 * @template TSearchParams - Type extending TableSearchParamsModel with optional additional filter properties
 * @param searchParams - Object containing MRT_PaginationState, MRT_SortingState, and custom filter model.
 *
 * @returns An object containing:
 * - `paginationModel` - MUI pagination model with 0-indexed page and pageSize (undefined if page/pageSize not provided)
 * - `sortModel` - MUI sort model array with field and sort direction (undefined if sortBy/sortOrder not provided)
 * - `filterModel` - Object containing all additional properties from searchParams (excluding pagination and sort fields)
 */
export function getMRTModelsFromSearchParams<TSearchParams extends TableSearchParamsModel>(
  searchParams: TSearchParams
) {
  const { page, pageSize, sortBy, sortOrder, ...rest } = searchParams;

  // MRT uses 0 based page index so subtract one
  const paginationModel: MRT_PaginationState | undefined =
    page && pageSize ? { pageIndex: page - 1, pageSize } : undefined;
  const sortModel: MRT_SortingState | undefined =
    sortBy && sortOrder ? [{ id: sortBy, desc: sortOrder === 'desc' }] : undefined;
  const filterModel = decodeDayFilters(rest);

  return {
    paginationModel,
    sortModel,
    filterModel,
  };
}

/**
 * Custom hook that synchronizes MUI DataGrid table state and custom filter state with URL search parameters.
 *
 * @param params - Configuration object
 * @param params.navigate - Navigation function to update URL search parameters (from TanStack Router)
 * @param params.paginationModel - Optional MUI DataGrid pagination model with 0-indexed page
 * @param params.sortModel - Optional MUI DataGrid sort model array
 * @param params.filterModel - Optional filter model object with custom filter properties
 *
 * @remarks
 * - Only the first sort field from the sort model is used in URL params
 * - Empty arrays in the filter model are excluded from URL params
 * - Uses `replace: true` for navigation to avoid cluttering browser history
 */
export function useSyncTableSearchParams<TFilterModel extends Record<string, any>>({
  navigate,
  paginationModel,
  sortModel,
  filterModel,
}: {
  navigate: (...args: any[]) => any;
  paginationModel?: GridPaginationModel;
  sortModel?: GridSortModel;
  filterModel?: TFilterModel;
}) {
  // Store a reference to the previous search params
  const prevParamsRef = useRef<string>();
  // Track if this is the first render
  const isFirstRenderRef = useRef(true);

  useEffect(() => {
    // Only use the first value from MUI sort model (we do not use secondary table sort)
    const sortBy = sortModel?.[0]?.field;
    const sortOrder = sortModel?.[0]?.sort;

    // Filter out empty arrays and empty strings from the filter model
    const transformedFilter = filterModel
      ? Object.fromEntries(
          Object.entries(filterModel)
            .filter(
              ([_, value]) =>
                (typeof value === 'string' && value !== '') ||
                ((!Array.isArray(value) || value.length > 0) && typeof value !== 'string')
            )
            .map(([key, value]) => {
              if (value instanceof Date) {
                // Drop invalid dates (e.g. a half-typed picker value) so they never reach
                // format(), which throws on an Invalid Date; write valid days as yyyy-MM-dd.
                if (Number.isNaN(value.getTime())) return [key, undefined];
                return [key, fDate(value, 'yyyy-MM-dd')];
              }
              return [key, value];
            })
        )
      : undefined;

    const newParams = {
      page: paginationModel ? paginationModel.page + 1 : undefined, // Convert 0-indexed to 1-indexed
      pageSize: paginationModel ? paginationModel.pageSize : undefined,
      sortBy,
      sortOrder,
      ...transformedFilter,
    };

    // Stringify the params to keep track of the previous state
    const newParamsString = JSON.stringify(newParams);

    // Skip sync on first render to prevent reordering URL params
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false;
      prevParamsRef.current = newParamsString;
      return;
    }

    // Only update search params if they have changed. This prevents unnecessary re-renders.
    if (prevParamsRef.current !== newParamsString) {
      prevParamsRef.current = newParamsString;
      navigate({
        replace: true,
        search: () => newParams,
      });
    }
  }, [navigate, filterModel, paginationModel, sortModel]);
}

/**
 * Custom hook that synchronizes material react table state and custom filter state with URL search parameters.
 *
 * @param params - Configuration object
 * @param params.navigate - Navigation function to update URL search parameters (from TanStack Router)
 * @param params.paginationModel - Optional MUI DataGrid pagination model with 0-indexed page
 * @param params.sortModel - Optional MUI DataGrid sort model array
 * @param params.filterModel - Optional filter model object with custom filter properties
 *
 * @remarks
 * - Only the first sort field from the sort model is used in URL params
 * - Empty arrays in the filter model are excluded from URL params
 * - Uses `replace: true` for navigation to avoid cluttering browser history
 */
export function useSyncMRTSearchParams<TFilterModel extends Record<string, any>>({
  navigate,
  paginationModel,
  sortModel,
  filterModel,
}: {
  navigate: (...args: any[]) => any;
  paginationModel?: MRT_PaginationState;
  sortModel?: MRT_SortingState;
  filterModel?: TFilterModel;
}) {
  // Store a reference to the previous search params
  const prevParamsRef = useRef<string>();
  // Track if this is the first render
  const isFirstRenderRef = useRef(true);

  useEffect(() => {
    // Only use the first value from MUI sort model (we do not use secondary table sort)
    const sortBy = sortModel?.[0]?.id;
    const sortOrder = sortModel?.[0]?.desc ? 'desc' : 'asc';

    // Filter out empty arrays and empty strings from the filter model
    const transformedFilter = filterModel
      ? Object.fromEntries(
          Object.entries(filterModel)
            .filter(
              ([_, value]) =>
                (typeof value === 'string' && value !== '') ||
                ((!Array.isArray(value) || value.length > 0) && typeof value !== 'string')
            )
            .map(([key, value]) => {
              if (value instanceof Date) {
                // Drop invalid dates (e.g. a half-typed picker value) so they never reach
                // format(), which throws on an Invalid Date; write valid days as yyyy-MM-dd.
                if (Number.isNaN(value.getTime())) return [key, undefined];
                return [key, fDate(value, 'yyyy-MM-dd')];
              }
              return [key, value];
            })
        )
      : undefined;

    const newParams = {
      page: paginationModel ? paginationModel.pageIndex + 1 : undefined, // Convert 0-indexed to 1-indexed
      pageSize: paginationModel ? paginationModel.pageSize : undefined,
      sortBy,
      sortOrder,
      ...transformedFilter,
    };

    // Stringify the params to keep track of the previous state
    const newParamsString = JSON.stringify(newParams);

    // Skip sync on first render to prevent reordering URL params
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false;
      prevParamsRef.current = newParamsString;
      return;
    }

    // Only update search params if they have changed. This prevents unnecessary re-renders.
    if (prevParamsRef.current !== newParamsString) {
      prevParamsRef.current = newParamsString;
      navigate({
        replace: true,
        search: () => newParams,
      });
    }
  }, [navigate, filterModel, paginationModel, sortModel]);
}
